// src/components/account/AvatarCard.jsx
import React, { useMemo, useRef, useState } from "react";

function toInitials(name, email) {
  const n = String(name || "").trim();

  // If we have a name, use first letters of first + last words
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || "";
    const b = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
    const initials = (a + b).toUpperCase();
    return initials || "U";
  }

  // If no name, try email
  const e = String(email || "").trim();
  if (e) {
    const user = e.split("@")[0] || "";
    const chars = user.replace(/[^a-zA-Z0-9]/g, "");
    const a = chars[0] || "U";
    const b = chars[1] || "";
    return (a + b).toUpperCase();
  }

  return "U";
}

/**
 * AvatarCard
 *
 * Mobile-first profile photo card.
 * • Renders the avatar image when available with an onError fallback to initials.
 * • Shows an accessible tap/click overlay to trigger a new upload.
 * • Accepts a hidden <input type="file"> ref from the parent or creates its own.
 */
export default function AvatarCard({
  name,
  email,
  avatarUrl,
  uploading,
  onPickFile,
  // Optional: pass a controlled fileInputRef from the parent.
  // When omitted, AvatarCard manages its own hidden input.
  fileInputRef: externalRef,
}) {
  const initials = useMemo(() => toInitials(name, email), [name, email]);
  const [imgError, setImgError] = useState(false);

  // Reset error state whenever avatarUrl changes (new upload or fresh load)
  const prevUrl = useRef(avatarUrl);
  if (prevUrl.current !== avatarUrl) {
    prevUrl.current = avatarUrl;
    if (imgError) setImgError(false);
  }

  // Internal file input — only used when the parent doesn't pass one
  const internalRef = useRef(null);

  const handleInternalChange = (e) => {
    if (!externalRef && onPickFile) {
      const file = e.target.files?.[0];
      if (file) onPickFile(file);
      // Reset so the same file can be picked again
      e.target.value = "";
    }
  };

  const showImage = !!(avatarUrl && !imgError);

  const border = "border-neutral-200 dark:border-neutral-800";
  const bg = "bg-white dark:bg-neutral-900";
  const avatarRing = "border-neutral-200 dark:border-neutral-800";
  const initialsBg = "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-50";

  return (
    <div className={`rounded-2xl border p-4 ${border} ${bg}`}>
      {/* Hidden file input — only rendered if parent didn't provide one */}
      {!externalRef && (
        <input
          ref={internalRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
          onChange={handleInternalChange}
        />
      )}

      <div className="flex flex-col items-center gap-3">
        {/* Avatar button — tap area is generous for mobile */}
        <button
          type="button"
          onClick={onPickFile}
          disabled={uploading}
          className={`relative h-24 w-24 overflow-hidden rounded-2xl border ${avatarRing} disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500`}
          aria-label="Change profile photo"
          title="Change profile photo"
        >
          {showImage ? (
            <img
              src={avatarUrl}
              alt="Profile avatar"
              className="h-full w-full object-cover"
              draggable={false}
              onError={() => setImgError(true)}
            />
          ) : (
            <div className={`grid h-full w-full place-items-center ${initialsBg}`}>
              <span className="text-2xl font-bold tracking-wide select-none">{initials}</span>
            </div>
          )}

          {/* Overlay label */}
          <div className="absolute inset-x-0 bottom-0 bg-black/50 py-1 text-[10px] font-semibold tracking-[0.18em] text-white text-center">
            {uploading ? "UPLOADING…" : "CHANGE"}
          </div>

          {/* Spinner overlay while uploading */}
          {uploading && (
            <div className="absolute inset-0 grid place-items-center bg-black/30">
              <svg
                className="h-7 w-7 animate-spin text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
          )}
        </button>

        {/* Secondary text hint — visible on mobile without a hover state */}
        <p className="text-[11px] text-center text-neutral-500 dark:text-neutral-400">
          JPG, PNG or WEBP · max 5 MB
        </p>
      </div>
    </div>
  );
}
